import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { getStatus, getIntegration, save as saveDestination } from "../../services/dataExportService";

const TYPES = [
  { value: "s3", label: "S3" },
  { value: "gcs", label: "GCS" },
];

// Scoped keyframes — the app-wide App.css (which defines a "spin" keyframe)
// is never imported anywhere, so a global name can't be relied on here.
const SPINNER_KEYFRAMES = `
  @keyframes dataExportSpin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
`;

function Spinner({ color = "#F90091", size = 10 }) {
  return (
    <span style={{
      display: "inline-block", boxSizing: "border-box", width: size, height: size,
      border: `2px solid ${color}`, borderTopColor: "transparent",
      borderRadius: "50%", animation: "dataExportSpin 0.7s linear infinite",
      flexShrink: 0,
    }} />
  );
}

const FIELD_LABEL_STYLE = { fontSize: 11, fontWeight: 600, color: "#626262", textTransform: "uppercase", letterSpacing: 0.6 };

function fieldInputStyle(hasError) {
  return { width: "100%", boxSizing: "border-box", marginTop: 4, padding: "8px 10px", borderRadius: 8, border: `1px solid ${hasError ? "#e53935" : "#E2E2E2"}`, fontSize: 13 };
}

function parseGcsJson(text) {
  if (!text || !text.trim()) return { value: null, error: null };
  try {
    const parsed = JSON.parse(text);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed) || Object.keys(parsed).length === 0) {
      return { value: null, error: "Service account JSON looks empty or invalid" };
    }
    return { value: parsed, error: null };
  } catch {
    return { value: null, error: "Could not parse as JSON" };
  }
}

export function DataExportIntegrationCard({ advertiserId, authToken, active }) {
  // advertiserId last successfully fetched for — not a boolean, so a
  // support-user masquerade switch (which changes advertiserId without
  // unmounting this component) correctly triggers a re-fetch instead of
  // leaving the previous advertiser's integration state stuck on screen.
  const fetchedForRef = useRef(null);
  // Caches decoded credentials per integration uuid so reopening the
  // "Manage" form doesn't re-fetch from portal-api every time — kept in
  // sync with what was last saved instead of invalidated.
  const credentialsCacheRef = useRef({});
  // Generation token for fetchStatus — bumped whenever advertiserId changes
  // so a response that was already in flight for the previous advertiser
  // can't land after the switch and overwrite the new advertiser's state.
  const statusRequestRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [integration, setIntegration] = useState(null); // null = not configured

  const [formOpen, setFormOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [type, setType] = useState("s3");
  const [location, setLocation] = useState("");
  const [awsAccessKey, setAwsAccessKey] = useState("");
  const [awsSecretKey, setAwsSecretKey] = useState("");
  const [gcsCredentialsText, setGcsCredentialsText] = useState("");
  const [formStatus, setFormStatus] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // Reset advertiser-scoped state whenever advertiserId changes (e.g.
  // support masquerade switching to a different advertiser) so stale data
  // — and a stale integration uuid a save could otherwise submit against
  // the new advertiser — never carries over.
  useEffect(() => {
    statusRequestRef.current += 1;
    setIntegration(null);
    setError(null);
    setFormOpen(false);
    setFormError(null);
    credentialsCacheRef.current = {};
    fetchedForRef.current = null;
  }, [advertiserId]);

  const fetchStatus = useCallback(async () => {
    if (!advertiserId || !authToken) return;
    const requestId = ++statusRequestRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await getStatus(advertiserId, authToken);
      if (requestId === statusRequestRef.current) {
        setIntegration(result);
      }
    } catch (err) {
      if (requestId === statusRequestRef.current) {
        setError(err?.message ?? "Failed to load data export status");
      }
    } finally {
      if (requestId === statusRequestRef.current) {
        setLoading(false);
      }
    }
  }, [advertiserId, authToken]);

  useEffect(() => {
    if (!active || !advertiserId || !authToken) return;
    if (fetchedForRef.current === advertiserId) return;
    fetchedForRef.current = advertiserId;
    fetchStatus();
  }, [active, advertiserId, authToken, fetchStatus]);

  // openForm already sets type/location/status from `integration` (the
  // list-endpoint result) — this only needs to fill in the credential
  // fields, which are the one thing that endpoint doesn't return.
  function applyCredentials(full) {
    if (!full) return;
    if (full.type === "s3") {
      setAwsAccessKey(full.credentials?.access ?? "");
      setAwsSecretKey(full.credentials?.secret ?? "");
    } else if (full.type === "gcs") {
      setGcsCredentialsText(full.credentials ? JSON.stringify(full.credentials, null, 2) : "");
    }
  }

  async function openForm() {
    setType(integration?.type ?? "s3");
    setLocation(integration?.location ?? "");
    setAwsAccessKey("");
    setAwsSecretKey("");
    setGcsCredentialsText("");
    setFormStatus(integration?.status ?? true);
    setFormError(null);
    setFormOpen(true);

    if (!integration?.uuid) return;

    const cached = credentialsCacheRef.current[integration.uuid];
    if (cached) {
      applyCredentials(cached);
      return;
    }

    setFormLoading(true);
    try {
      const full = await getIntegration(advertiserId, authToken, { integrationUuid: integration.uuid });
      if (!full) {
        setFormError("Saved credentials are unavailable. Re-enter them to continue.");
        return;
      }
      credentialsCacheRef.current[integration.uuid] = full;
      applyCredentials(full);
    } catch (err) {
      setFormError(err?.message ?? "Failed to load existing credentials");
    } finally {
      setFormLoading(false);
    }
  }

  function closeForm() {
    setFormOpen(false);
    setFormError(null);
  }

  const gcsParsed = useMemo(() => parseGcsJson(gcsCredentialsText), [gcsCredentialsText]);
  const hasErrors =
    !location.trim() ||
    (type === "s3" && (!awsAccessKey.trim() || !awsSecretKey.trim())) ||
    (type === "gcs" && (!gcsCredentialsText.trim() || !!gcsParsed.error));

  async function handleGcsFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setGcsCredentialsText(text);
      setFormError(null);
    } catch {
      setFormError("Could not read the selected file.");
    } finally {
      e.target.value = "";
    }
  }

  async function handleSubmit() {
    if (hasErrors || saving) return;
    setSaving(true);
    setFormError(null);
    try {
      // The Edge Function nests these per portal-api's expected shape — the
      // frontend only deals in a flat, type-agnostic credentials object.
      const credentials =
        type === "s3"
          ? { access: awsAccessKey.trim(), secret: awsSecretKey.trim() }
          : gcsParsed.value;

      const payload = { type, location: location.trim(), credentials, status: formStatus };
      const result = await saveDestination(advertiserId, authToken, {
        ...payload,
        integrationUuid: integration?.uuid,
      });
      setIntegration(prev => ({ ...prev, ...result }));
      if (result?.uuid) {
        credentialsCacheRef.current[result.uuid] = payload;
      }
      setFormOpen(false);
    } catch (err) {
      setFormError(err?.message ?? "Failed to save data export destination");
    } finally {
      setSaving(false);
    }
  }

  const isConnected = !!integration;
  // Single source of truth for the loading/error/connected/empty branching
  // used by both the status text and the action button below.
  const cardState = loading ? "loading" : error ? "error" : isConnected ? "connected" : "empty";

  return (
    <div style={{
      background: "#FFFFFF",
      border: `1px solid ${isConnected && integration.status ? "#F9009133" : "#E2E2E2"}`,
      borderRadius: 12,
      overflow: "hidden",
      marginTop: 12,
    }}>
      <style>{SPINNER_KEYFRAMES}</style>
      <div style={{ padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{
          width: 42, height: 42, borderRadius: 10,
          background: "#EFF6FF", border: "1px solid #BFDBFE",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M12 3v12m0 0l-4-4m4 4l4-4" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600, color: "#151515", fontSize: 14, lineHeight: 1.2 }}>Data Export</span>
            <span style={{
              fontSize: 9, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase",
              color: "#7b7b7b", background: "#F5F5F5", padding: "2px 6px", borderRadius: 4,
            }}>S3 / GCS</span>
          </div>
          {cardState === "loading" ? (
            <div style={{ height: 14, width: 120, background: "#F0F0F0", borderRadius: 4 }} />
          ) : cardState === "error" ? (
            <span style={{ fontSize: 11, color: "#e53935" }}>{error}</span>
          ) : cardState === "connected" ? (
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: integration.status ? "#16a34a" : "#F59E0B", display: "inline-block", flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: "#3E3E3E", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {integration.status ? "Active" : "Inactive"}
              </span>
            </div>
          ) : null}
        </div>

        <div style={{ flexShrink: 0, paddingTop: 2 }}>
          {cardState === "loading" ? (
            <div style={{ width: 125, height: 30, background: "#F0F0F0", borderRadius: 8 }} />
          ) : cardState === "error" ? (
            <button
              onClick={fetchStatus}
              style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #E2E2E2", fontSize: 12, fontWeight: 500, cursor: "pointer", background: "#FAFAFA", color: "#3E3E3E", minWidth: 125, whiteSpace: "nowrap" }}
            >
              Retry
            </button>
          ) : cardState === "connected" ? (
            <button
              onClick={openForm}
              style={{ padding: "6px 14px", borderRadius: 8, border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", background: "#F90091", color: "#fff", minWidth: 125, whiteSpace: "nowrap" }}
            >
              Manage
            </button>
          ) : (
            <button
              onClick={openForm}
              style={{ padding: "6px 14px", borderRadius: 8, border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", background: "#F90091", color: "#fff", minWidth: 125, whiteSpace: "nowrap" }}
            >
              Connect
            </button>
          )}
        </div>
      </div>

      {cardState === "empty" && !formOpen && (
        <div style={{ borderTop: "1px solid #F0F0F0", padding: "10px 16px" }}>
          <span style={{ fontSize: 12, color: "#7b7b7b", lineHeight: 1.5 }}>
            Export impression and conversion data to your own S3 or GCS bucket.
          </span>
        </div>
      )}

      {formOpen && (
        <div style={{ borderTop: "1px solid #F0F0F0", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
          {formLoading && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#7b7b7b" }}>
              <Spinner size={9} />
              Loading saved credentials…
            </div>
          )}

          <div style={{ display: "flex", gap: 8 }}>
            {TYPES.map(t => (
              <button
                key={t.value}
                onClick={() => setType(t.value)}
                style={{
                  flex: 1, padding: "7px 10px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
                  whiteSpace: "nowrap",
                  border: `1px solid ${type === t.value ? "#F90091" : "#E2E2E2"}`,
                  background: type === t.value ? "#FFF5FB" : "#FAFAFA",
                  color: type === t.value ? "#F90091" : "#3E3E3E",
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div>
            <label htmlFor="data-export-location" style={FIELD_LABEL_STYLE}>Location</label>
            <input
              id="data-export-location"
              type="text"
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder="bucket-name/folder"
              style={fieldInputStyle(!location.trim())}
            />
          </div>

          {type === "s3" ? (
            <>
              <div>
                <label htmlFor="data-export-access-key" style={FIELD_LABEL_STYLE}>Access key</label>
                <input
                  id="data-export-access-key"
                  type="text"
                  value={awsAccessKey}
                  onChange={e => setAwsAccessKey(e.target.value)}
                  style={fieldInputStyle(!awsAccessKey.trim())}
                />
              </div>
              <div>
                <label htmlFor="data-export-secret-key" style={FIELD_LABEL_STYLE}>Secret key</label>
                <input
                  id="data-export-secret-key"
                  type="password"
                  value={awsSecretKey}
                  onChange={e => setAwsSecretKey(e.target.value)}
                  style={fieldInputStyle(!awsSecretKey.trim())}
                />
              </div>
            </>
          ) : (
            <div>
              <label htmlFor="data-export-gcs-json" style={FIELD_LABEL_STYLE}>Service account JSON</label>
              <input
                type="file"
                accept=".json,application/json"
                onChange={handleGcsFile}
                aria-label="Upload service account JSON file"
                style={{ marginTop: 4, marginBottom: 6, fontSize: 12 }}
              />
              <textarea
                id="data-export-gcs-json"
                value={gcsCredentialsText}
                onChange={e => setGcsCredentialsText(e.target.value)}
                placeholder="Paste service account JSON here"
                rows={4}
                style={{ ...fieldInputStyle(!!gcsParsed.error), marginTop: 0, fontFamily: "monospace", fontSize: 12 }}
              />
              {gcsParsed.error && (
                <div style={{ fontSize: 11, color: "#e53935", marginTop: 4 }}>{gcsParsed.error}</div>
              )}
            </div>
          )}

          {integration && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                type="button"
                role="switch"
                aria-checked={formStatus}
                onClick={() => setFormStatus(s => !s)}
                aria-label="Integration active"
                style={{
                  width: 28, height: 16, borderRadius: 999, flexShrink: 0, padding: 2,
                  border: "none", cursor: "pointer",
                  background: formStatus ? "#F90091" : "#D9D9D9",
                  display: "flex", alignItems: "center",
                  justifyContent: formStatus ? "flex-end" : "flex-start",
                }}
              >
                <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff", display: "block" }} />
              </button>
              <span style={FIELD_LABEL_STYLE}>{formStatus ? "Active" : "Inactive"}</span>
            </div>
          )}

          {formError && (
            <div style={{ fontSize: 12, color: "#e53935" }}>{formError}</div>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
            <button
              onClick={closeForm}
              disabled={saving}
              style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #E2E2E2", fontSize: 12, fontWeight: 500, cursor: saving ? "default" : "pointer", background: "#FAFAFA", color: "#3E3E3E" }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={hasErrors || saving || formLoading}
              style={{
                padding: "7px 16px", borderRadius: 8, border: "none", fontSize: 12, fontWeight: 600,
                cursor: hasErrors || saving || formLoading ? "default" : "pointer",
                background: hasErrors || saving || formLoading ? "#F5B8D8" : "#F90091",
                color: "#fff",
                display: "flex", alignItems: "center", gap: 6,
              }}
            >
              {saving && <Spinner color="#fff" size={10} />}
              {saving ? "Testing connection…" : integration ? "Update" : "Connect"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
